import { useEffect, useSyncExternalStore } from 'react';
import { To } from 'react-router-dom';

import { getDocumentTitle } from '@utils/documentUtils';

export type DocumentTitleSegment = {
  route?: string | To;
  title: string;
};

type Listener = () => void;

let documentTitleSegments: DocumentTitleSegment[] = [];
const listeners = new Set<Listener>();

const getSegments = () => documentTitleSegments;

const areSegmentsEqual = (a: DocumentTitleSegment[], b: DocumentTitleSegment[]) =>
  a.length === b.length &&
  a.every((segment, index) => {
    const candidate = b[index];
    return segment.title === candidate?.title && segment.route === candidate?.route;
  });

export const setDocumentTitleSegments = (segments: DocumentTitleSegment[]) => {
  if (areSegmentsEqual(segments, documentTitleSegments)) return;
  documentTitleSegments = segments;
  listeners.forEach(listener => listener());
};

export const useDocumentTitleSegments = () =>
  useSyncExternalStore(
    listener => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getSegments,
    getSegments
  );

export const useDocumentTitleValue = () => getDocumentTitle(useDocumentTitleSegments());

export const useDocumentTitle = (...segments: (DocumentTitleSegment | undefined)[]) => {
  useEffect(() => {
    const nextSegments = segments.filter(Boolean) as DocumentTitleSegment[];
    setDocumentTitleSegments(nextSegments);
  }, [segments]);
};

const useDocumentContext = () => {
  const documentTitleSegments = useDocumentTitleSegments();
  return {
    documentTitle: getDocumentTitle(documentTitleSegments),
    documentTitleSegments,
    setDocumentTitleSegments,
  };
};

export default useDocumentContext;
